// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/first-sync-gate/offline-sync-status.ts
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/shared/src/powersync/offline-sync-status.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo daily-journal-app
//     node fleet/fleet.ts check --repo daily-journal-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
/**
 * "Has this local database EVER completed a sync?" — answered from SQLite, not
 * from the live sync status.
 *
 * ─── Why this exists ────────────────────────────────────────────────────────
 *
 * The first-sync overlay must never show for a returning user. The live signal
 * it used (`aggregateStreamProgress(status.syncStreams).hasSynced`) cannot tell
 * a returning user apart from a brand-new one during a cold start:
 *
 *   - Under Tauri, the bridge reports `internalStreamSubscriptions: []` until
 *     the sync client receives its first status line (see the
 *     `beforeFirstStatusLine` entry of the captured Tauri fixture). An empty
 *     list is correctly read as "not synced", so the overlay's state machine
 *     walks into `connecting` on EVERY app start — and stays there for as long
 *     as there is no network.
 *   - On web, `db.connect()` replaces the status with the sync client's, whose
 *     stream list is also empty until the core emits its first status.
 *
 * The data needed to answer the question is already persisted by the PowerSync
 * core extension: `powersync_offline_sync_status()` returns the same status
 * shape the core emits while syncing, built from the local tables, including
 * `last_synced_at` per priority and per stream. The web SDK itself calls it on
 * init to populate `SyncStatus.hasSynced`. It is a plain SQL function, so it
 * works identically through the web WASM build and the Tauri plugin — one code
 * path, no `isTauriRuntime()` branch.
 *
 * Because the answer lives in the same database as the data, it is correct by
 * construction across sign-out (`disconnectAndClear()` wipes both) and across
 * accounts (each account has its own database).
 */

/**
 * `FULL_SYNC_PRIORITY` from `@powersync/common`: the priority entry that marks
 * a COMPLETE sync, as opposed to a partial (priority-bounded) checkpoint.
 */
export const FULL_SYNC_PRIORITY = 2_147_483_647;

/** The slice of the core's offline status JSON this module reads. */
interface CoreOfflineStatus {
  priority_status?: readonly { priority?: number; last_synced_at?: number | null }[] | null;
  streams?: readonly { last_synced_at?: number | null }[] | null;
}

function isTimestamp(value: number | null | undefined): value is number {
  return typeof value === "number";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * True when the core's offline status says this database has completed at
 * least one sync.
 *
 * Two independent signals, either is enough:
 *   1. the FULL-sync priority entry carries a `last_synced_at`;
 *   2. EVERY tracked stream carries a `last_synced_at`. An empty stream list is
 *      `false`, never `true` — `[].every()` is vacuously true and would declare
 *      a brand-new database synced (same trap as `streamsHaveSynced`).
 *
 * Accepts the raw column value (a JSON string) or an already-parsed object.
 * Anything unparsable is `false`: the caller then falls back to the live
 * status, which is exactly the behaviour before this module existed.
 */
export function hasCompletedSyncFromOfflineStatus(raw: unknown): boolean {
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return false;
    }
  }
  if (!isRecord(parsed)) {
    return false;
  }
  const status = parsed as CoreOfflineStatus;

  const full = status.priority_status?.find((entry) => entry.priority === FULL_SYNC_PRIORITY);
  if (isTimestamp(full?.last_synced_at)) {
    return true;
  }

  const { streams } = status;
  if (!streams || streams.length === 0) {
    return false;
  }
  return streams.every((stream) => isTimestamp(stream.last_synced_at));
}

/** The single method this module needs from a PowerSync database. */
export interface OfflineStatusReader {
  get: <T>(sql: string, parameters?: unknown[]) => Promise<T>;
}

/**
 * Reads the persisted "has ever synced" flag from the local database.
 *
 * Resolves to `null` when the question cannot be answered (the query failed,
 * e.g. a core extension too old to expose the function). `null` means
 * "unknown": the caller must fall back to the live status, NOT assume either
 * answer.
 */
export async function readEverSynced(db: OfflineStatusReader): Promise<boolean | null> {
  try {
    const row = await db.get<{ r: unknown }>("SELECT powersync_offline_sync_status() AS r");
    return hasCompletedSyncFromOfflineStatus(row.r);
  } catch {
    return null;
  }
}
